import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { translations, LANGUAGES } from "@/i18n/translations";

const LanguageContext = createContext(null);
const STORAGE_KEY = "fuditap_lang";

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem(STORAGE_KEY) || "tr");

  const isRTL = useMemo(() => LANGUAGES.find((l) => l.code === lang)?.rtl || false, [lang]);

  const applyDir = useCallback((code) => {
    const rtl = LANGUAGES.find((l) => l.code === code)?.rtl || false;
    document.documentElement.lang = code;
    document.documentElement.dir = rtl ? "rtl" : "ltr";
  }, []);

  const setLang = useCallback((code) => {
    setLangState(code);
    localStorage.setItem(STORAGE_KEY, code);
    applyDir(code);
  }, [applyDir]);

  useEffect(() => { applyDir(lang); }, [lang, applyDir]);

  const t = useCallback(
    (key) => translations[lang]?.[key] ?? translations.en[key] ?? translations.tr[key] ?? key,
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t, isRTL }), [lang, setLang, t, isRTL]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
